import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

/**
 * Settings API client (SCRUM-460/461) over the SCRUM-458/459 backend. The
 * /api/settings/* surface is TENANT-SCOPED — the tenant is derived server-side
 * from the authenticated caller's App ID token (tenant_id claim, SCRUM-102).
 *
 * AUTHORIZATION LIMITATION (plan RT3): the write surface is server-side open
 * until SCRUM-103 — the Bank Executive gate is UI-only (SideNav footer
 * visibility). Stated in SETTINGS.md.
 */
const ORGANIZATION_URL = '/api/settings/organization';
const PERMISSIONS_URL = '/api/settings/permissions';
const USERS_URL = '/api/settings/users';
// ↑ Swap-point: nginx proxies /api/* to bankiq-api in the deployed container.

// SCRUM-643: the hard-coded DEMO_TENANT_ID header was removed. Authenticated
// /api/* calls carry the App ID Bearer (apiFetch), and the server derives the
// tenant STRICTLY from the validated token's tenant_id claim
// (TenantResolutionMiddleware, SCRUM-102) — a client-supplied X-Tenant-Id is
// ignored for authenticated callers. So no tenant header is sent here.
const tenantHeader = {} as const;
const jsonHeaders = { 'Content-Type': 'application/json' } as const;

const errorSchema = z.object({ error: z.string(), message: z.string() });

async function throwError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const parsed = errorSchema.safeParse(await res.json());
    if (parsed.success) message = parsed.data.message;
  } catch {
    // Non-JSON body — keep the fallback.
  }
  throw new Error(message);
}

// ── Organization (SCRUM-460) ──────────────────────────────────────────────────

const organizationSchema = z.object({
  bankName: z.string(),
  primaryAdminName: z.string(),
  primaryAdminEmail: z.string(),
  supportPhone: z.string().nullable(),
  primaryAdminUserId: z.string().nullable(),
});

export type SettingsOrganization = z.infer<typeof organizationSchema>;

export interface OrganizationPatch {
  bankName?: string | null;
  supportPhone?: string | null;
  primaryAdminUserId?: string | null;
}

export async function fetchOrganization(): Promise<SettingsOrganization> {
  const res = await apiFetch(ORGANIZATION_URL, { headers: tenantHeader });
  if (!res.ok) await throwError(res, 'Failed to load organization settings.');
  return organizationSchema.parse(await res.json());
}

export async function patchOrganization(patch: OrganizationPatch): Promise<void> {
  const res = await apiFetch(ORGANIZATION_URL, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify(patch),
  });
  if (!res.ok) await throwError(res, 'Failed to save organization settings.');
}

// ── Permissions matrix (SCRUM-461) ────────────────────────────────────────────

const permissionsSchema = z.object({ matrixJson: z.string() });

/** Role → { module: bool } grant map, parsed from the stored matrixJson. */
export type PermissionMatrix = Record<string, Record<string, boolean>>;

export async function fetchPermissions(): Promise<PermissionMatrix> {
  const res = await apiFetch(PERMISSIONS_URL, { headers: tenantHeader });
  if (!res.ok) await throwError(res, 'Failed to load permissions.');
  const { matrixJson } = permissionsSchema.parse(await res.json());
  return JSON.parse(matrixJson) as PermissionMatrix;
}

export async function patchPermissions(matrix: PermissionMatrix): Promise<void> {
  const res = await apiFetch(PERMISSIONS_URL, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify({ matrixJson: JSON.stringify(matrix) }),
  });
  if (!res.ok) await throwError(res, 'Failed to save permissions.');
}

// ── Product priority (SCRUM-46) ───────────────────────────────────────────────

const PRODUCT_PRIORITY_URL = '/api/settings/product-priority';
const productPrioritySchema = z.object({ orderJson: z.string() });

/** The global product-priority order: an ordered list of product ids. */
export type ProductPriorityOrder = string[];

export async function fetchProductPriority(): Promise<ProductPriorityOrder> {
  const res = await apiFetch(PRODUCT_PRIORITY_URL, { headers: tenantHeader });
  if (!res.ok) await throwError(res, 'Failed to load product priority.');
  const { orderJson } = productPrioritySchema.parse(await res.json());
  const parsed: unknown = JSON.parse(orderJson);
  // Defensive: the API guarantees an array of strings, but guard the parse.
  return z.array(z.string()).catch([]).parse(parsed);
}

export async function patchProductPriority(order: ProductPriorityOrder): Promise<void> {
  const res = await apiFetch(PRODUCT_PRIORITY_URL, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify({ orderJson: JSON.stringify(order) }),
  });
  if (!res.ok) await throwError(res, 'Failed to save product priority.');
}

// ── Role name aliases (SCRUM-465) ─────────────────────────────────────────────

const ROLE_ALIASES_URL = '/api/settings/role-aliases';
const roleAliasesSchema = z.object({ aliasesJson: z.string() });

/**
 * Per-role display-name aliases: stored-SystemRole value → alias string. Only
 * the four bank-assignable roles are valid keys (Executive / BankOfficer /
 * BranchManager / BranchEmployee); an absent alias means "use the built-in
 * display name". Display-level only — never changes the stored role.
 */
export type RoleAliases = Record<string, string>;

export async function fetchRoleAliases(): Promise<RoleAliases> {
  const res = await apiFetch(ROLE_ALIASES_URL, { headers: tenantHeader });
  if (!res.ok) await throwError(res, 'Failed to load role aliases.');
  const { aliasesJson } = roleAliasesSchema.parse(await res.json());
  const parsed: unknown = JSON.parse(aliasesJson);
  // Defensive: the API guarantees an object of string values; guard the parse.
  return z.record(z.string(), z.string()).catch({}).parse(parsed);
}

export async function patchRoleAliases(aliases: RoleAliases): Promise<void> {
  const res = await apiFetch(ROLE_ALIASES_URL, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify({ aliasesJson: JSON.stringify(aliases) }),
  });
  if (!res.ok) await throwError(res, 'Failed to save role aliases.');
}

// ── Users (SCRUM-461) ─────────────────────────────────────────────────────────

const userSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.enum(['active', 'pending', 'deactivated']),
  branch: z.string().nullable(),
  title: z.string().nullable(),
  phone: z.string().nullable(),
});

export type SettingsUser = z.infer<typeof userSchema>;

export interface AddUserInput {
  displayName: string;
  email: string;
  title?: string | null;
  phone?: string | null;
}

export async function fetchUsers(): Promise<SettingsUser[]> {
  const res = await apiFetch(USERS_URL, { headers: tenantHeader });
  if (!res.ok) await throwError(res, 'Failed to load users.');
  return userSchema.array().parse(await res.json());
}

export async function addUser(input: AddUserInput): Promise<string> {
  const res = await apiFetch(USERS_URL, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  });
  if (!res.ok) await throwError(res, 'Failed to add user.');
  const parsed = z.object({ id: z.string() }).parse(await res.json());
  return parsed.id;
}

export async function assignUserRole(userId: string, role: string): Promise<void> {
  const res = await apiFetch(`${USERS_URL}/${userId}/role`, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify({ role }),
  });
  if (!res.ok) await throwError(res, 'Failed to assign role.');
}

export async function deactivateUser(userId: string): Promise<void> {
  const res = await apiFetch(`${USERS_URL}/${userId}/deactivate`, {
    method: 'POST',
    headers: tenantHeader,
  });
  if (!res.ok) await throwError(res, 'Failed to deactivate user.');
}
